import { noop, orderBy } from 'lodash';
import { useCallback, useMemo, useRef, useState } from 'react';

import {
  BulkSizeControls,
  TileSelectionBar,
  VerticalDivider,
} from '@/components/DiagramBar';
import { AlignmentPicker } from '@/components/diagrams/components/AlignmentPicker/AlignmentPicker';
import { DisplayAsPicker } from '@/components/diagrams/components/DisplayAsPicker/DisplayAsPicker';
import { IconShapePicker } from '@/components/diagrams/components/IconShapePicker/IconShapePicker';
import { TileEmphasisPicker } from '@/components/diagrams/components/TileEmphasisPicker/TileEmphasisPicker';
import { FlowChart, FlowChartProps } from '@/components/diagrams/FlowChart';
import { Background } from '@/components/diagrams/FlowChart/components/Background';
import { ControlButton } from '@/components/diagrams/FlowChart/components/Controls/ControlButton';
import { ControlButtonGroup } from '@/components/diagrams/FlowChart/components/Controls/ControlButtonGroup';
import { Controls } from '@/components/diagrams/FlowChart/components/Controls/Controls';
import { DownloadButton } from '@/components/diagrams/FlowChart/components/Controls/DownloadButton';
import { EditButton } from '@/components/diagrams/FlowChart/components/Controls/EditButton';
import { FitViewButton } from '@/components/diagrams/FlowChart/components/Controls/FitViewButton';
import { FullscreenButton } from '@/components/diagrams/FlowChart/components/Controls/FullscreenButton';
import { ZoomInButton } from '@/components/diagrams/FlowChart/components/Controls/ZoomInButton';
import { ZoomOutButton } from '@/components/diagrams/FlowChart/components/Controls/ZoomOutButton';
import { HelperLines } from '@/components/diagrams/FlowChart/components/HelperLines';
import { useFlowChartContext } from '@/components/diagrams/FlowChart/context/flowChart.context';
import { useAlignNodes } from '@/components/diagrams/FlowChart/hooks/useAlignNodes';
import { useGetFlowViewportCenter } from '@/components/diagrams/FlowChart/hooks/useGetFlowViewportCenter';
import { useIsInteractive } from '@/components/diagrams/FlowChart/hooks/useIsInteractive';
import { useSelectedComponentType } from '@/components/diagrams/FlowChart/hooks/useSelectedComponentType';
import { useSelectedIconShape } from '@/components/diagrams/FlowChart/hooks/useSelectedIconShape';
import { useSelectedTileEmphasis } from '@/components/diagrams/FlowChart/hooks/useSelectedTileEmphasis';
import { useUndoRedoKeyboardShortcuts } from '@/components/diagrams/FlowChart/hooks/useUndoRedoKeyboardShortcuts';
import {
  TEXT_NOTE_DEFAULT_HEIGHT,
  TEXT_NOTE_DEFAULT_WIDTH,
} from '@/components/diagrams/FlowChart/utils/textNoteUtils';
import { Button } from '@/components/form/baseInputs/Button';
import { UndoRedoButtonGroup } from '@/components/form/baseInputs/Button/UndoRedoButtonGroup';
import { ButtonGroup } from '@/components/form/baseInputs/ButtonGroup';
import { Switch } from '@/components/form/baseInputs/Switch/Switch';
import { LoadingOverlay } from '@/components/progress/LoadingOverlay/LoadingOverlay';
import { useViewOnly } from '@/contexts/InteractionParadigm.context';
import { useOnBeforeUnloadPrompt } from '@/hooks/useOnBeforeUnloadPrompt';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import {
  applyGroupToOtherSections,
  createTemporaryGroupId,
  VisualizationGroup,
} from '@/modules/graphViz/graphVizGroup/grouping';
import { VisualizationGroupModal } from '@/modules/graphViz/graphVizGroup/VisualizationGroupModal';
import { TILE_NOTES_BULK_PANEL_ID } from '@/modules/summaryPanels/TileNotesSummaryPanel/constants';
import { useTileNotesGenerationTarget } from '@/modules/summaryPanels/TileNotesSummaryPanel/hooks/useTileNotesGenerationTarget';
import { LuminaryThemeProvider } from '@/styles/themes/LuminaryThemeProvider';

import { ContextPrimaryClient } from '../household/contexts/householdDetails.context';
import { AutoPositionButton } from './components/AutoPositionButton';
import { EntityDiagramLegend } from './components/EntityDiagramLegend/EntityDiagramLegend';
import { EntityDiagramVizConfigSettings } from './components/EntityDiagramVizConfigSettings';
import { useEntityDiagramContext } from './contexts/entityDiagram.context';
import { useBulkTileSizeChange } from './hooks/useBulkTileSizeChange';
import { useFlowChartProps } from './hooks/useFlowChartProps';
import { useIsCompactLayout } from './hooks/useIsCompactLayout';
import { useNodeSelection } from './hooks/useNodeSelection';
import { EntityDiagramVariant } from './types';

export interface EntityDiagramFlowChartProps {
  entityId: string;
  householdId: string;
  entity: {
    subtype: {
      displayName: string;
    };
  };
  primaryClients: ContextPrimaryClient[];
  // presentationMode is used to disable interactivity with the graph
  presentationMode?: boolean;
  selectedPrimaryClientId: string;
  setSelectedPrimaryClientId?: (id: string) => void;
  entityDiagramVariant: EntityDiagramVariant;
  viewOnlyMode?: boolean;
  // When provided, clicking Edit opens a trowser instead of inline edit mode
  onEditClick?: () => void;
  hideFullscreenButton?: boolean;
}

export const EntityDiagramFlowChart = ({
  entityId,
  householdId,
  entity,
  primaryClients,
  presentationMode,
  selectedPrimaryClientId,
  setSelectedPrimaryClientId,
  entityDiagramVariant,
  viewOnlyMode,
  onEditClick,
  hideFullscreenButton,
}: EntityDiagramFlowChartProps) => {
  const isViewOnlyInteractionParadigm = useViewOnly();
  const hasEditPermission = useCanEditHouseholdData();
  const trackUserEvent = useTrackUserEvent();
  const { isInteractive } = useIsInteractive();
  const { dispatch, getState, state } = useEntityDiagramContext();
  const isCompactLayout = useIsCompactLayout();
  const getViewportCenter = useGetFlowViewportCenter();
  const { selectedNodes, selectedNodesForGrouping } = useNodeSelection();
  const {
    __internal: { setIsDirty },
  } = useFlowChartContext();
  const [activeGroup, setActiveGroup] = useState<VisualizationGroup | null>(
    null
  );
  const cancelGroupDrop = useRef<(() => void) | null>(null);
  const createGroupFromDrop = useCallback(
    (group: VisualizationGroup, onCancel: () => void) => {
      cancelGroupDrop.current = onCancel;
      setActiveGroup(group);
    },
    []
  );
  const renderGroupSettings = useCallback(
    (group: VisualizationGroup, onClose: () => void) => {
      return (
        <VisualizationGroupModal
          group={group}
          candidates={state.nodes.filter(
            (node) => node.data.sectionLabelId === group.namespace
          )}
          allowApplyToOtherSections={
            state.entityDiagramVariant === EntityDiagramVariant.Dispositions &&
            Object.keys(state.derivedData.nodesBySectionId).some(
              (section) => section !== group.namespace
            )
          }
          onClose={onClose}
          onDelete={() => {
            dispatch({
              type: 'SET_VISUALIZATION_GROUPS',
              groups: state.visualizationGroups.filter(
                (item) => item.id !== group.id
              ),
            });
            setIsDirty(true);
            onClose();
          }}
          onSave={(updatedGroup, applyToOtherSections) => {
            const mergePreviousDrag = cancelGroupDrop.current !== null;
            cancelGroupDrop.current = null;
            let groups = [
              ...state.visualizationGroups.filter(
                (item) => item.id !== updatedGroup.id
              ),
              updatedGroup,
            ];
            if (
              applyToOtherSections &&
              state.entityDiagramVariant === EntityDiagramVariant.Dispositions
            ) {
              groups = applyGroupToOtherSections(
                groups,
                group,
                updatedGroup,
                state.derivedData.nodesBySectionId
              );
            }
            dispatch({
              type: 'SET_VISUALIZATION_GROUPS',
              groups,
              mergePreviousDrag,
            });
            setIsDirty(true);
            onClose();
          }}
        />
      );
    },
    [
      dispatch,
      setIsDirty,
      state.nodes,
      state.entityDiagramVariant,
      state.visualizationGroups,
      state.derivedData.nodesBySectionId,
    ]
  );
  const {
    flowChartProps: baseFlowChartProps,
    helperLineHorizontal,
    helperLineVertical,
  } = useFlowChartProps(createGroupFromDrop, renderGroupSettings);
  const groupSelection = useCallback(() => {
    const namespace = selectedNodesForGrouping[0]?.data.sectionLabelId;
    if (selectedNodesForGrouping.length < 2 || typeof namespace !== 'string') {
      return;
    }
    setActiveGroup({
      id: createTemporaryGroupId(),
      displayName: '',
      namespace,
      nodeIds: selectedNodesForGrouping.map((node) =>
        node.id.split(':').slice(1).join(':')
      ),
    });
  }, [selectedNodesForGrouping]);
  const selectedGroup =
    selectedNodes.length === 1 && selectedNodes[0]?.data.isGroupNode
      ? state.visualizationGroups.find(
          (group) => group.id === selectedNodes[0]?.data.visualizationGroupId
        )
      : undefined;
  const {
    hasIconTilesSelected,
    currentShape: currentIconShape,
    isMixed: isIconShapeMixed,
    setIconShape: handleIconShapeChange,
  } = useSelectedIconShape(state.nodes);
  const {
    hasEmphasizableTilesSelected,
    currentEmphasis: currentTileEmphasis,
    isMixed: isTileEmphasisMixed,
    setTileEmphasis: handleTileEmphasisChange,
  } = useSelectedTileEmphasis(state.nodes);
  const {
    hasConvertibleTilesSelected,
    currentType: currentComponentType,
    isMixed: isComponentTypeMixed,
    setComponentType: handleComponentTypeChange,
  } = useSelectedComponentType(state.nodes);
  const {
    canIncrease: canIncreaseTileSize,
    canDecrease: canDecreaseTileSize,
    eligibleTileCount: bulkSizeEligibleCount,
    handleBulkIncrease,
    handleBulkDecrease,
  } = useBulkTileSizeChange();
  const {
    eligibleCount: alignEligibleCount,
    canAlign,
    canDistribute,
    alignNodes,
    distributeNodes,
  } = useAlignNodes(state.nodes);
  // Align / shape / emphasis / display-as form one logical group that shares a
  // single leading divider (no dividers between the pickers), matching the
  // estate waterfall.
  const showDisplayGroup =
    canAlign ||
    hasIconTilesSelected ||
    hasEmphasizableTilesSelected ||
    hasConvertibleTilesSelected;
  const notesTarget = useTileNotesGenerationTarget();
  const handleGenerateNotes = useCallback(() => {
    if (!notesTarget) return;
    trackUserEvent(notesTarget.trackingEvent, {
      source: 'TileSelectionBar',
      tileCount: selectedNodes.length,
      ...notesTarget.trackingProps,
    });
    dispatch({ type: 'OPEN_SUMMARY_PANEL', nodeId: TILE_NOTES_BULK_PANEL_ID });
  }, [notesTarget, selectedNodes.length, trackUserEvent, dispatch]);
  const prevRenderWhiteLabeledRef = useRef(false);
  const isViewOnly = isViewOnlyInteractionParadigm || viewOnlyMode;
  const canUndo = state.nodeHistory.past.length > 0;
  const canRedo = state.nodeHistory.future.length > 0;

  const handleUndo = useCallback(() => {
    dispatch({ type: 'UNDO_HISTORY' });
  }, [dispatch]);

  const handleRedo = useCallback(() => {
    dispatch({ type: 'REDO_HISTORY' });
  }, [dispatch]);

  useUndoRedoKeyboardShortcuts({
    onUndo: handleUndo,
    onRedo: handleRedo,
    canUndo,
    canRedo,
  });

  const {
    isOnDispositionsFormDiagram,
    bdgDefaultTriggerClientId,
    isBdgEntity,
  } = getState();

  useOnBeforeUnloadPrompt(isInteractive);

  const buttonGroupOptions = useMemo(() => {
    return orderBy(primaryClients, (c) => c.updatedAt, 'desc').map(
      (client) => ({
        display: `${client.displayGivenName} dies first`,
        value: client.id,
      })
    );
  }, [primaryClients]);

  // BDG death-order Switch replaces the ButtonGroup for retirement /
  // DH-insurance entities. OFF position uses the calculated default
  // trigger when there is one (single owner / policy holder who is a
  // primary client) and falls back to primaryClients[0] otherwise
  // (survivorship, multi-holder, non-primary-client owner).
  const bdgSwitchProps = useMemo(() => {
    if (!isBdgEntity) return null;
    const defaultClientId =
      bdgDefaultTriggerClientId ?? primaryClients[0]?.id ?? null;
    if (!defaultClientId) return null;
    const alternate = primaryClients.find((c) => c.id !== defaultClientId);
    if (!alternate) return null;
    return {
      defaultClientId,
      alternate,
      isOn: selectedPrimaryClientId === alternate.id,
    };
  }, [
    bdgDefaultTriggerClientId,
    isBdgEntity,
    primaryClients,
    selectedPrimaryClientId,
  ]);

  const flowChartProps: FlowChartProps = {
    ...baseFlowChartProps,
    // Disable panning and zooming in presentation mode
    ...(presentationMode && {
      panOnDrag: false,
      zoomOnScroll: false,
      zoomOnDoubleClick: false,
      zoomOnPinch: false,
      zoomActivationKeyCode: null,
      onNodeClick: noop,
      elementsSelectable: false,
    }),
    // Enable node dragging when in edit mode
    ...(isInteractive &&
      !presentationMode && {
        nodesDraggable: true,
        nodesConnectable: false,
        elementsSelectable: true,
      }),
  };

  const handleSelectPrimaryClient = (primaryClientId: string) => {
    const validPrimaryClientIds = primaryClients.map((client) => client.id);

    if (!validPrimaryClientIds.includes(primaryClientId)) {
      return;
    }

    if (primaryClientId === selectedPrimaryClientId) {
      return;
    }

    setSelectedPrimaryClientId?.(primaryClientId);
  };

  return (
    <>
      {flowChartProps.style?.opacity === 0 && !isViewOnly && (
        <LoadingOverlay open={true} updatingText="Loading..." />
      )}
      <FlowChart
        {...flowChartProps}
        presentationMode={presentationMode}
        edgeCursorPointer={!isOnDispositionsFormDiagram}
      >
        <Background />
        {isInteractive && !isOnDispositionsFormDiagram && (
          <HelperLines
            vertical={helperLineVertical}
            horizontal={helperLineHorizontal}
          />
        )}
        {!presentationMode && (
          <LuminaryThemeProvider>
            <Controls position="top-left">
              {!isInteractive &&
                entityDiagramVariant === EntityDiagramVariant.Dispositions &&
                setSelectedPrimaryClientId &&
                buttonGroupOptions.length > 1 &&
                (bdgSwitchProps ? (
                  <Switch
                    label={`Show ${bdgSwitchProps.alternate.displayGivenName} dies first`}
                    labelPosition="right"
                    value={bdgSwitchProps.isOn}
                    onChange={(e) => {
                      handleSelectPrimaryClient(
                        e.target.checked
                          ? bdgSwitchProps.alternate.id
                          : bdgSwitchProps.defaultClientId
                      );
                    }}
                    testId="bdg-death-order-switch"
                  />
                ) : (
                  <ButtonGroup<string>
                    label="Primary Client"
                    hideLabel
                    options={buttonGroupOptions}
                    value={selectedPrimaryClientId}
                    onChange={(_, value) => {
                      handleSelectPrimaryClient(value);
                    }}
                    sx={{
                      whiteSpace: 'nowrap',
                    }}
                  />
                ))}
              <AutoPositionButton />
              {isInteractive && <EntityDiagramVizConfigSettings />}
              {isInteractive && (
                <ControlButton
                  variant="secondary"
                  square={false}
                  onClick={() => {
                    const center = getViewportCenter();
                    dispatch({
                      type: 'ADD_TEXT_NOTE',
                      position: {
                        x: center.x - TEXT_NOTE_DEFAULT_WIDTH / 2,
                        y: center.y - TEXT_NOTE_DEFAULT_HEIGHT / 2,
                      },
                    });
                  }}
                >
                  Add note
                </ControlButton>
              )}
            </Controls>
            {isInteractive && (
              <Controls position="top-center">
                <TileSelectionBar
                  selectedCount={selectedNodes.length}
                  isCompact={isCompactLayout}
                  groupEligibleCount={selectedNodesForGrouping.length}
                  onGroupTiles={groupSelection}
                  onGenerateNotes={
                    notesTarget ? handleGenerateNotes : undefined
                  }
                >
                  {selectedGroup && (
                    <Button
                      variant="transparent"
                      size="xs"
                      onClick={() => setActiveGroup(selectedGroup)}
                    >
                      Group settings
                    </Button>
                  )}
                  {showDisplayGroup && <VerticalDivider />}
                  {canAlign && (
                    <AlignmentPicker
                      onAlign={(kind) => {
                        trackUserEvent('entity_diagram align_clicked', {
                          source: 'entity_diagram',
                          kind,
                          nodeCount: alignEligibleCount,
                        });
                        alignNodes(kind);
                      }}
                      onDistribute={(axis) => {
                        trackUserEvent('entity_diagram distribute_clicked', {
                          source: 'entity_diagram',
                          axis,
                          nodeCount: alignEligibleCount,
                        });
                        distributeNodes(axis);
                      }}
                      canDistribute={canDistribute}
                    />
                  )}
                  {hasIconTilesSelected && (
                    <IconShapePicker
                      currentShape={currentIconShape}
                      isMixed={isIconShapeMixed}
                      onShapeChange={handleIconShapeChange}
                    />
                  )}
                  {hasEmphasizableTilesSelected && (
                    <TileEmphasisPicker
                      currentEmphasis={currentTileEmphasis}
                      isMixed={isTileEmphasisMixed}
                      onEmphasisChange={handleTileEmphasisChange}
                    />
                  )}
                  {hasConvertibleTilesSelected && (
                    <DisplayAsPicker
                      currentType={currentComponentType}
                      isMixed={isComponentTypeMixed}
                      onTypeChange={handleComponentTypeChange}
                    />
                  )}
                  {bulkSizeEligibleCount > 0 && (
                    <>
                      <VerticalDivider />
                      <BulkSizeControls
                        onIncrease={handleBulkIncrease}
                        onDecrease={handleBulkDecrease}
                        canIncrease={canIncreaseTileSize}
                        canDecrease={canDecreaseTileSize}
                      />
                    </>
                  )}
                </TileSelectionBar>
              </Controls>
            )}
            {activeGroup &&
              renderGroupSettings(activeGroup, () => {
                // Cancel a drop-created group and restore the tile's position.
                cancelGroupDrop.current?.();
                cancelGroupDrop.current = null;
                setActiveGroup(null);
              })}
            <Controls>
              {isInteractive && (
                <UndoRedoButtonGroup
                  onUndo={handleUndo}
                  onRedo={handleRedo}
                  canUndo={canUndo}
                  canRedo={canRedo}
                  isCompact={isCompactLayout}
                />
              )}
              {/* Edit button opens trowser; only shown on main page (when onEditClick provided) and not already editing */}
              {!isViewOnly &&
                hasEditPermission &&
                onEditClick &&
                !isInteractive && <EditButton onClick={onEditClick} />}
              <DownloadButton
                viewDisplayName={entity.subtype.displayName}
                householdId={householdId}
                onBeforeDownload={async () => {
                  prevRenderWhiteLabeledRef.current =
                    getState().renderWhiteLabeled;
                  dispatch({
                    type: 'START_EXPORT',
                  });
                  dispatch({
                    type: 'SET_RENDER_WHITE_LABELED',
                    renderWhiteLabeled: true,
                  });
                  // Wait for the entity diagram to update before exporting the image
                  await new Promise((res) => setTimeout(res, 100));
                }}
                onAfterDownload={() => {
                  dispatch({
                    type: 'SET_RENDER_WHITE_LABELED',
                    renderWhiteLabeled: prevRenderWhiteLabeledRef.current,
                  });
                  dispatch({ type: 'END_EXPORT' });
                  trackUserEvent('entity diagram exported', {
                    householdId,
                    entityId,
                  });
                }}
                tooltipText="Download entity diagram as PNG"
              />
              <ControlButtonGroup>
                {!hideFullscreenButton && (
                  <FullscreenButton name="entityDiagram" />
                )}
                <FitViewButton />
                <ZoomInButton />
                <ZoomOutButton />
              </ControlButtonGroup>
            </Controls>
            <Controls position="bottom-left" style={{ margin: 0 }}>
              <EntityDiagramLegend
                presentationMode={presentationMode}
                viewOnlyMode={viewOnlyMode}
              />
            </Controls>
          </LuminaryThemeProvider>
        )}
      </FlowChart>
    </>
  );
};
